'use client';

import { useState, useEffect } from 'react';
import Image from 'next/image';
import { motion, useReducedMotion } from 'motion/react';
import { InView, Stagger, StaggerChild } from '@/components/motion/InView';
import { localized, type HomeContentConfig, type RestaurantLocale } from '@restaurant/config';
import styles from './RestaurantIntroSection.module.css';

const EASE = [0.22, 1, 0.36, 1] as const;

interface RestaurantIntroSectionProps {
  content: HomeContentConfig['restaurantIntro'];
  locale: RestaurantLocale;
}

export function RestaurantIntroSection({ content, locale }: RestaurantIntroSectionProps) {
  const reduced = useReducedMotion();
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);

  return (
    <section className={styles.section}>
      <div className={styles.container}>
        {/* Text column */}
        <div className={styles.content}>
          <InView delay={0} y={20}>
            <span className={styles.eyebrow}>{localized(content.eyebrow, locale)}</span>
          </InView>
          <InView delay={0.1} y={28}>
            <h2 className={styles.title}>{localized(content.title, locale)}</h2>
          </InView>
          <InView delay={0.2} y={24}>
            <p className={styles.text}>{localized(content.body, locale)}</p>
          </InView>
          <Stagger as="ul" className={styles.featureList} stagger={0.08} delayChildren={0.3}>
            {content.features.map((feature, index) => (
              <StaggerChild as="li" className={styles.feature} key={`${feature.fr}-${index}`} y={16}>
                <span className={styles.dot} aria-hidden="true" />
                <span>{localized(feature, locale)}</span>
              </StaggerChild>
            ))}
          </Stagger>
        </div>

        {/* Image duo — clip-path curtain reveal (client only) */}
        <div className={styles.imageDuo}>
          {mounted && !reduced ? (
            <>
              <motion.div
                className={styles.largeImage}
                initial={{ clipPath: 'inset(0 100% 0 0)', scale: 1.06 }}
                whileInView={{ clipPath: 'inset(0 0% 0 0)', scale: 1 }}
                viewport={{ once: true, margin: '-60px' }}
                transition={{ duration: 1.0, delay: 0.1, ease: EASE }}
              >
                <Image
                  src={content.imageUrl}
                  alt={localized(content.imageAlt, locale)}
                  fill
                  quality={70}
                  sizes="(max-width: 560px) 100vw, (max-width: 920px) 52vw, 24vw"
                />
              </motion.div>
              <motion.div
                className={styles.smallImage}
                initial={{ opacity: 0, y: 40, scale: 0.96 }}
                whileInView={{ opacity: 1, y: 0, scale: 1 }}
                viewport={{ once: true, margin: '-60px' }}
                transition={{ duration: 0.9, delay: 0.45, ease: EASE }}
              >
                <Image
                  src={content.secondaryImageUrl}
                  alt={localized(content.secondaryImageAlt, locale)}
                  fill
                  quality={70}
                  sizes="(max-width: 560px) 100vw, (max-width: 920px) 70vw, 32vw"
                />
              </motion.div>
            </>
          ) : (
            <>
              <div className={styles.largeImage}>
                <Image
                  src={content.imageUrl}
                  alt={localized(content.imageAlt, locale)}
                  fill
                  quality={70}
                  sizes="(max-width: 560px) 100vw, (max-width: 920px) 52vw, 24vw"
                />
              </div>
              <div className={styles.smallImage}>
                <Image
                  src={content.secondaryImageUrl}
                  alt={localized(content.secondaryImageAlt, locale)}
                  fill
                  quality={70}
                  sizes="(max-width: 560px) 100vw, (max-width: 920px) 70vw, 32vw"
                />
              </div>
            </>
          )}
        </div>
      </div>
    </section>
  );
}
